/**
 * VerificationBanner — the "please verify your email" prompt shown at the top
 * of the authenticated app for users whose email isn't verified yet.
 *
 * Extracted from App.js (shell split) — BYTE-IDENTICAL body. Rendered in
 * AuthenticatedApp when `user.is_email_verified === false`. Offers a resend
 * action (POST /auth/send-verification) and a dismiss.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
// Direct import, NOT the './ui' barrel: this banner is in App.js's EAGER
// entry graph, and eager modules bypass the barrel so a future heavy
// re-export can't leak into the entry chunk (same rule as AgeGateModal).
import Button from './ui/Button';

function VerificationBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/v1/auth/send-verification`);
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      console.error('Failed to resend verification email:', err);
    } finally {
      setSending(false);
    }
  };

  if (dismissed) return null;

  return (
    // UX sweep P3 (bug-hunt 2026-07-12): role="status" (polite), not
    // role="alert" (assertive/interruptive). This is a STANDING,
    // dismissible reminder — not an urgent system error worth cutting off
    // a screen-reader user's current task for. role="alert" is reserved
    // for time-sensitive system failures elsewhere in the app.
    <div className="verification-banner" role="status" aria-live="polite">
      <span className="verification-banner-text">Please verify your email address.</span>
      <Button
        variant="encourage"
        size="sm"
        onClick={handleResend}
        disabled={sending || sent}
      >
        {sent ? 'Sent!' : sending ? 'Sending...' : 'Resend email'}
      </Button>
      <button
        className="verification-banner-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        &#10005;
      </button>
    </div>
  );
}

export default VerificationBanner;
